import { useEffect, useRef } from "react";
import type { RemoteActionId } from "./remoteMap.js";

/**
 * Where a remote action goes. Each part of the app answers the actions it
 * owns — the router the pages, the Live page the channel changes, the player
 * its menus — so a mapped button reaches the same code its on-screen button
 * does, and nothing is duplicated here.
 *
 * A handler returns false when the action does not apply right now (Programme
 * info off the Live page, Seek on live TV); the next one is asked, and when
 * none takes it the press is reported as unused.
 */
type Handler = () => boolean | void;

/** Internal: leave full screen, asked for by the navigation actions before they move. */
export type InternalActionId = RemoteActionId | "exitFullscreen";

const handlers = new Map<InternalActionId, Handler[]>();

export function onRemoteAction(id: InternalActionId, handler: Handler): () => void {
  const list = handlers.get(id) ?? [];
  list.push(handler);
  handlers.set(id, list);
  return () => {
    const current = handlers.get(id) ?? [];
    handlers.set(
      id,
      current.filter((other) => other !== handler),
    );
  };
}

/** Run an action; true when something took it. The newest handler is asked first. */
export function runRemoteAction(id: InternalActionId): boolean {
  if (id === "none") return true;
  const list = [...(handlers.get(id) ?? [])].reverse();
  for (const handler of list) if (handler() !== false) return true;
  return false;
}

/**
 * Answer actions for as long as the component is mounted. The handlers are
 * read through a ref, so a fresh closure on every render is fine.
 */
export function useRemoteActions(map: Partial<Record<InternalActionId, Handler>>): void {
  const current = useRef(map);
  current.current = map;
  const ids = Object.keys(map).sort().join(",");
  useEffect(() => {
    // A handler that returns nothing TOOK the action; only an explicit false
    // passes it on. "?? false" turned every taken action into a refusal, and
    // once a refused custom button fell back to its own job, Up set to
    // Programme info opened the panel AND changed the channel (owner,
    // 2026-10-06).
    const offs = (ids ? ids.split(",") : []).map((id) =>
      onRemoteAction(id as InternalActionId, () => {
        const handler = current.current[id as InternalActionId];
        return handler ? handler() : false;
      }),
    );
    return () => offs.forEach((off) => off());
  }, [ids]);
}

/** Tests only. */
export function resetRemoteActions(): void {
  handlers.clear();
}
